import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { RiAlarmWarningLine, RiCloseLine, RiNotification3Line, RiVolumeMuteLine, RiVolumeUpLine } from '@remixicon/react';
import { DEFAULT_ALARM_SETTINGS, type AccessibleTenant, type AlarmEventDto } from '@atnt/shared';
import { ApiError } from '@/shared/api/client';
import { Button, IconButton, toast } from '@/shared/ui';
// deep import: the shell mounts this component, and the engine barrel would pull the whole constructor into it
import { formatDateTime } from '@/features/constructor/engine/live/format';
import { alarmKeys, useAcknowledgeAlarm, useAlarmSettings, useAlarmSummary } from './api';
import { alarmFeed } from './feed';
import { canAckAlarms, canViewAlarms, formatValue, indicatorTitle, LEVEL_COLOR } from './model';
import { alarmSound } from './sound';

const MAX_BANNERS = 3;

/**
 * Alarm presence in the app shell (03-modules §8.2 [P]): the bell with the unacknowledged counter (header right end;
 * 497:21047 "notification-3-line" 32 ghost + orange counter), the live alarm feed, sticky banners for newly opened
 * events ("M-2 (Avariya oldi): HA-01 Beton ichi — 25.4 °C" with "Ko'rish" / "Tasdiqlash") and the alarm tone — each
 * following the tenant's "Mezon sozlamalari" (on screen / sound, sound type, repeat count, duration).
 */
export function AlarmCenter({ tenant }: { tenant: AccessibleTenant }) {
  if (!canViewAlarms(tenant)) return null;
  return <AlarmCenterInner key={tenant.id} tenant={tenant} />;
}

const subscribeSound = (cb: () => void) => alarmSound.onChange(cb);

function AlarmCenterInner({ tenant }: { tenant: AccessibleTenant }) {
  const { t } = useTranslation('alarms');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const summary = useAlarmSummary(tenant.id);
  const settings = useAlarmSettings(tenant.id);
  const [banners, setBanners] = useState<AlarmEventDto[]>([]);
  const settingsRef = useRef(settings.data);
  useLayoutEffect(() => {
    settingsRef.current = settings.data;
  });

  useEffect(() => {
    alarmSound.install();
    return alarmFeed.subscribe(tenant.id, (msg) => {
      void qc.invalidateQueries({ queryKey: alarmKeys.all(tenant.id), predicate: (q) => q.queryKey[3] !== 'settings' });
      if (msg.type !== 'alarm') return;
      const e = msg.event;
      if (msg.action === 'opened') {
        const s = settingsRef.current?.[e.level] ?? DEFAULT_ALARM_SETTINGS[e.level];
        if (s.showOnScreen) setBanners((list) => [e, ...list.filter((x) => x.indicatorId !== e.indicatorId)].slice(0, MAX_BANNERS));
        if (s.playSound) alarmSound.play(e.id, s, e.level === 'm2' ? 2 : 1);
      } else {
        setBanners((list) => list.filter((x) => x.id !== e.id));
        alarmSound.stop(e.id);
      }
    });
  }, [tenant.id, qc]);

  const unack = summary.data?.summary.unacknowledged ?? 0;
  const openAlarms = () => void navigate({ to: '/t/$tenantSlug/alarms', params: { tenantSlug: tenant.slug }, search: { status: 'open' } });

  return (
    <>
      <span aria-hidden className="h-16 w-px shrink-0 bg-line [:empty+&]:hidden" />
      <IconButton
        icon={RiNotification3Line}
        size="md"
        aria-label={t('bell.label')}
        badge={unack}
        tooltip={unack ? t('bell.count', { count: unack }) : t('bell.label')}
        onClick={openAlarms}
        className="-my-4"
      />
      {banners.length > 0 &&
        createPortal(
          <AlarmBanners
            tenant={tenant}
            events={banners}
            onClose={(id) => {
              setBanners((list) => list.filter((x) => x.id !== id));
              alarmSound.stop(id);
            }}
            onView={() => {
              alarmSound.stop();
              openAlarms();
            }}
          />,
          document.body,
        )}
    </>
  );
}

function AlarmBanners({
  tenant,
  events,
  onClose,
  onView,
}: {
  tenant: AccessibleTenant;
  events: readonly AlarmEventDto[];
  onClose: (id: string) => void;
  onView: () => void;
}) {
  const { t, i18n } = useTranslation('alarms');
  const locale = i18n.language;
  const ack = useAcknowledgeAlarm(tenant.id);
  const canAck = canAckAlarms(tenant);
  const playing = useSyncExternalStore(subscribeSound, () => alarmSound.playing);
  const blocked = useSyncExternalStore(subscribeSound, () => alarmSound.blocked);

  return (
    <section aria-label={t('banner.region')} aria-live="assertive" className="pointer-events-none fixed top-84 left-1/2 z-80 flex w-480 max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col gap-8">
      {events.map((e) => {
        const color = LEVEL_COLOR[e.level];
        const value = formatValue(e.peakValue ?? e.threshold, e, locale);
        const threshold = formatValue(e.threshold, e, locale);
        return (
          <article
            key={e.id}
            role="alert"
            className="pointer-events-auto relative flex gap-12 overflow-hidden rounded-8 border bg-panel py-12 pr-12 pl-16 shadow-float"
            style={{ borderColor: `color-mix(in srgb, ${color} 45%, transparent)` }}
          >
            <span aria-hidden className="absolute inset-y-0 left-0 w-3" style={{ backgroundColor: color }} />
            <span
              aria-hidden
              className="flex size-32 shrink-0 items-center justify-center rounded-8"
              style={{ color, backgroundColor: `color-mix(in srgb, ${color} 20%, transparent)` }}
            >
              <RiAlarmWarningLine className="size-18" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              <p className="text-sm font-semibold whitespace-pre-wrap text-fg">
                <span style={{ color }}>{t(`levels.${e.level}`)}</span>: {indicatorTitle(e)} — {value}
              </p>
              <p className="text-xs text-fg-secondary">
                {t('banner.started', { time: formatDateTime(e.startedAt) })} · {t('banner.threshold', { value: threshold })}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-8">
                <Button size="sm" variant="secondary" onClick={onView}>
                  {t('banner.view')}
                </Button>
                {canAck && (
                  <Button
                    size="sm"
                    variant="primary-alt"
                    loading={ack.isPending && ack.variables === e.id}
                    onClick={() =>
                      ack.mutate(e.id, {
                        onSuccess: () => {
                          onClose(e.id);
                          toast.success(t('ack.done'));
                        },
                        onError: (err) => toast.error(err instanceof ApiError ? err.message : t('error.generic')),
                      })
                    }
                  >
                    {t('banner.ack')}
                  </Button>
                )}
                {blocked && (
                  <Button size="sm" variant="link" leftIcon={RiVolumeUpLine} onClick={() => void alarmSound.resume()} title={t('banner.blocked')}>
                    {t('banner.enableSound')}
                  </Button>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-center gap-4">
              <IconButton icon={RiCloseLine} size="sm" aria-label={t('banner.close')} onClick={() => onClose(e.id)} />
              {playing === e.id && <IconButton icon={RiVolumeMuteLine} size="sm" aria-label={t('banner.mute')} tooltip onClick={() => alarmSound.stop(e.id)} />}
            </div>
          </article>
        );
      })}
    </section>
  );
}
